import { useRef, useEffect } from "react";
import { Bold, Type as TypeIcon, Eraser } from "lucide-react";

const FONTS = [
  { label: "Default", value: "" },
  { label: "Serif (Cinzel)", value: "'Cinzel', serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Sans (Arial)", value: "Arial, Helvetica, sans-serif" },
  { label: "Courier", value: "'Courier New', monospace" },
];

const COLORS = ["#1A1A1A", "#560216", "#9a3b3b", "#3a6b45", "#8a6d3b", "#4a6fa5", "#737373"];

// Rich text editor with color/font/bold and preserved blank lines. Outputs HTML.
export const RichTextEditor = ({ value, onChange, placeholder }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current.innerHTML);

  const exec = (cmd, arg = null) => {
    ref.current.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand(cmd, false, arg);
    emit();
  };

  return (
    <div className="border border-[#E5E5E5] rounded-md overflow-hidden" data-testid="rich-desc-editor">
      <div className="flex flex-wrap items-center gap-2 border-b border-[#E5E5E5] bg-[#faf8f5] px-3 py-2">
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} className="p-1.5 rounded hover:bg-[#eee7dd]" title="Bold" data-testid="rich-bold">
          <Bold strokeWidth={2} className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-1">
          <TypeIcon strokeWidth={1.5} className="h-4 w-4 text-[#737373]" />
          <select
            onChange={(e) => { exec("fontName", e.target.value); e.target.selectedIndex = 0; }}
            className="text-xs border border-[#E5E5E5] rounded px-2 py-1 bg-white outline-none"
            data-testid="rich-font"
            defaultValue=""
          >
            {FONTS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-1.5 pl-1">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); exec("foreColor", c); }}
              className="h-5 w-5 rounded-full border border-black/10"
              style={{ backgroundColor: c }}
              title={`Colour ${c}`}
              data-testid={`rich-color-${c.replace('#','')}`}
            />
          ))}
          <label className="h-5 w-5 rounded-full border border-black/10 overflow-hidden relative cursor-pointer" title="Custom colour">
            <span className="absolute inset-0" style={{ background: "conic-gradient(red,orange,yellow,lime,cyan,blue,magenta,red)" }} />
            <input type="color" onChange={(e) => exec("foreColor", e.target.value)} className="opacity-0 absolute inset-0 cursor-pointer" data-testid="rich-color-custom" />
          </label>
        </div>

        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); }} className="p-1.5 rounded hover:bg-[#eee7dd] ml-auto" title="Clear formatting" data-testid="rich-clear">
          <Eraser strokeWidth={1.5} className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder || "Write a description…"}
        className="min-h-[120px] px-4 py-3 text-sm outline-none leading-relaxed whitespace-pre-wrap lira-rich-input"
        style={{ whiteSpace: "pre-wrap" }}
        data-testid="rich-desc-input"
      />
    </div>
  );
};

export default RichTextEditor;
